import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchClients } from "../api/lge.ts";
import type { TabIdentity } from "./GovernanceTab.tsx";

// Market Health on the Tiger substrate (increment 3a/3b + LIQ-63).
// Instrument-aware: renders the CEX diagnostics component and the DEX health
// component independently. A CEX diagnostics error is a card, not a dead
// page; pack selection + RFP generation stay available whenever either
// component has data (a DEX mandate generates off DEX data).

const TEXT = "#cfe0ff", MUTED = "#8a93a6", AMBER = "#e0a458", GREEN = "#6fcf97",
  RED = "#e07070", ACCENT = "#6699ff", BORDER = "rgba(255,255,255,0.08)",
  CARD = "rgba(255,255,255,0.03)";

const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "14px 16px" } as const;
const kpi = { fontSize: 26, fontWeight: 700, color: TEXT,
  fontFamily: "SF Mono, Consolas, monospace" } as const;
const lbl = { fontSize: 11, color: MUTED, textTransform: "uppercase",
  letterSpacing: 0.6, marginBottom: 6 } as const;

async function fetchHealth(cid: string) {
  const r = await fetch(`/api/v1/governance/clients/${encodeURIComponent(cid)}/health`,
    { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`Health request failed (${r.status})`);
  return r.json();
}

const fmtUsd = (v: number | null | undefined) =>
  v == null ? "—" : `$${Math.round(v).toLocaleString()}`;
const oppColor = (o?: string) =>
  o === "HIGH" ? RED : o === "MODERATE" ? AMBER : GREEN;

export function GovernanceHealthView({ identity }: { identity: TabIdentity }) {
  const isClient = identity.role !== "operator";
  const [cid, setCid] = useState<string | null>(isClient ? identity.client_id : null);
  const clientsQ = useQuery({
    queryKey: ["lge", "clients"], queryFn: fetchClients, enabled: !isClient,
  });
  const healthQ = useQuery({
    queryKey: ["lge", "health", cid], queryFn: () => fetchHealth(cid!), enabled: !!cid,
    staleTime: 5 * 60 * 1000,
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {!isClient && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(clientsQ.data?.clients ?? [])
            .filter((c) => c.category === "active")
            .map((c) => (
              <button key={c.client_id} onClick={() => setCid(c.client_id)}
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "12px 16px", textAlign: "left", fontSize: 14, fontWeight: 700, color: "#6699ff", cursor: "pointer" }}>
                {c.display_name}
              </button>
            ))}
        </div>
      )}
      {!cid && <div style={{ color: MUTED, padding: 24, fontSize: 13 }}>
        Select an active client to compute market health.</div>}
      {cid && healthQ.isLoading && <div style={{ color: MUTED, padding: 24, fontSize: 13 }}>
        Computing diagnostics on the substrate…</div>}
      {cid && healthQ.isError && <div style={{ color: RED, padding: 24, fontSize: 13 }}>
        {healthQ.error instanceof Error ? healthQ.error.message : "Failed"}</div>}
      {cid && healthQ.data && <Payload d={healthQ.data} />}
    </div>
  );
}

function Payload({ d }: { d: any }) {
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const diag = d.diagnostics;
  const dexProfiles = (d.dex_health?.profiles ?? []) as any[];
  const hasDex = dexProfiles.length > 0;
  const canGenerate = !!diag || hasDex;

  const recs = (d.recommendation?.recommendations ?? []) as any[];
  const top = d.recommendation?.top_recommendation;
  const availablePacks = (d.available_packs ?? []) as string[];

  return (
    <>
      {/* CEX diagnostics component — error is a card, never a dead page */}
      {!diag && (
        <div style={{ ...card, color: AMBER, fontSize: 13 }}>
          CEX diagnostics unavailable: {d.diagnostics_error}
          <div style={{ color: MUTED, marginTop: 6, fontSize: 12 }}>{d.retention_note}</div>
          {hasDex && (
            <div style={{ color: MUTED, marginTop: 6, fontSize: 12 }}>
              DEX pool health below is unaffected — a DEX engagement can be
              configured and generated from on-chain data.
            </div>
          )}
        </div>
      )}
      {diag && <CexDiagnostics d={d} diag={diag} />}

      {/* DEX health component */}
      {d.dex_health_error && (
        <div style={{ ...card, color: AMBER, fontSize: 13 }}>
          DEX health unavailable: {d.dex_health_error}
        </div>
      )}
      {hasDex && <DexHealth dex={d.dex_health} />}

      {/* Pack selection + RFP configuration — available with either component */}
      {canGenerate && (
        <div style={card}>
          <div style={lbl}>Pack recommendation</div>
          {!selectedPack && (
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
              Select a pack to open RFP configuration and generate an engagement.
            </div>
          )}
          {recs.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: 10 }}>
              {[...recs].sort((a, b) => (b.fit_score ?? 0) - (a.fit_score ?? 0)).map((r, i) => {
                const isTop = r.pack_id === top;
                const isSel = selectedPack === r.pack_id;
                return (
                  <div key={r.pack_id}
                    onClick={() => setSelectedPack(r.pack_id)}
                    style={{ border: `1px solid ${isSel ? ACCENT : isTop ? GREEN : BORDER}`,
                      background: isSel ? "rgba(102,153,255,0.10)" : "transparent",
                      cursor: "pointer", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: MUTED }}>#{i + 1} · {Math.round((r.fit_score ?? 0) * 100)}% fit</span>
                      {isTop && <span style={{ fontSize: 10, color: GREEN, border: `1px solid ${GREEN}55`,
                        borderRadius: 4, padding: "1px 6px" }}>RECOMMENDED</span>}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 6 }}>
                      {String(r.pack_id ?? "").replaceAll("_", " ")}</div>
                    {(r.rules_matched ?? []).map((m: any) => (
                      <div key={m.rule_id} style={{ fontSize: 11, color: m.matched ? MUTED : "rgba(138,147,166,0.45)",
                        marginBottom: 3, lineHeight: 1.4 }}>
                        {m.matched ? "✓" : "✗"} {m.detail ?? m.description}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                Data-driven pack recommendations require CEX diagnostics; select
                an objective pack directly.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {availablePacks.map((p) => {
                  const isSel = selectedPack === p;
                  return (
                    <button key={p} onClick={() => setSelectedPack(p)}
                      style={{ fontSize: 13, fontWeight: 700, padding: "8px 14px",
                        borderRadius: 8, cursor: "pointer", color: TEXT,
                        border: `1px solid ${isSel ? ACCENT : BORDER}`,
                        background: isSel ? "rgba(102,153,255,0.10)" : "transparent" }}>
                      {p.replaceAll("_", " ")}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {selectedPack && (
            <RfpConfigForm clientId={d.client_id} packId={selectedPack}
              rfpVenues={d.rfp_venues ?? []} />
          )}
        </div>
      )}
    </>
  );
}

function CexDiagnostics({ d, diag }: { d: any; diag: any }) {
  const sp = diag.spread_profile ?? {}, dp = diag.depth_profile ?? {},
    vc = diag.venue_concentration ?? {}, w = diag.analysis_window ?? {},
    vol = diag.volatility_regime ?? {},
    perVenueSp = sp.per_venue ?? {},
    band = (b: string) => dp.per_bps_band?.[b]?.median_usd;
  const shares: [string, number][] = Object.entries(vc.venue_volume_shares ?? {});
  shares.sort((a, b) => b[1] - a[1]);
  const dominant = shares[0];
  return (
    <>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 5, color: TEXT,
          border: `1px solid ${BORDER}` }}>
          VOLATILITY REGIME: <b>{(vol.classification ?? "unknown").toUpperCase()}</b>
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>
          {d.token_pair} · window {w.duration_days ?? "—"}d · {w.venue_count ?? 0} venues ·
          sufficiency: {diag.data_sufficiency ?? "—"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 12 }}>
        <div style={card}>
          <div style={lbl}>Spread health</div>
          <div style={kpi}>{sp.median_spread_bps ?? "—"} bps
            <span style={{ fontSize: 13, color: MUTED }}> (p25: {sp.p25_spread_bps ?? "—"})</span></div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
            p75 {sp.p75_spread_bps ?? "—"} · p95 {sp.p95_spread_bps ?? "—"} ·
            stability {sp.spread_stability_index ?? "—"}</div>
        </div>
        <div style={card}>
          <div style={lbl}>Depth health (50bps)</div>
          <div style={kpi}>{fmtUsd(band("50"))}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
            100bps {fmtUsd(band("100"))} · 200bps {fmtUsd(band("200"))}</div>
        </div>
        <div style={card}>
          <div style={lbl}>Venue balance</div>
          <div style={kpi}>HHI {vc.herfindahl_index ?? "—"}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
            {dominant ? `Dominant: ${dominant[0]} (${dominant[1]}% volume)` : "No volume shares"}</div>
        </div>
        <div style={card}>
          <div style={lbl}>Data sufficiency</div>
          <div style={kpi}>{w.duration_days ?? "—"} days</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
            coverage {Math.min(100, w.data_coverage_pct ?? 0)}% · {d.retention_note}</div>
        </div>
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Venue", "Spread (median)", "Spread p25 (best)", "Depth (median)", "Depth p25 (floor)", "Opportunity", "Volume share"].map((h) => (
            <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11,
              color: MUTED, textTransform: "uppercase", letterSpacing: 0.5,
              borderBottom: `1px solid ${BORDER}` }}>{h}</th>))}</tr></thead>
          <tbody>
            {Object.entries(perVenueSp).map(([v, s]: [string, any]) => {
              const opp = d.recommendation?.health_summary?.spread_health?.per_venue?.[v]
                ?.improvement_opportunity;
              return (
                <tr key={v}>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: TEXT,
                    fontWeight: 600, borderBottom: `1px solid ${BORDER}` }}>{v}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: TEXT,
                    borderBottom: `1px solid ${BORDER}` }}>{s.median_spread_bps} bps</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: MUTED,
                    borderBottom: `1px solid ${BORDER}` }}>{s.p25_spread_bps} bps</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: TEXT,
                    borderBottom: `1px solid ${BORDER}` }}>{fmtUsd((dp.per_venue ?? {})[v]?.["50"]?.median_usd ?? null)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: MUTED,
                    borderBottom: `1px solid ${BORDER}` }}>{fmtUsd((dp.per_venue ?? {})[v]?.["50"]?.p25_usd ?? null)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, fontWeight: 700,
                    color: oppColor(opp), borderBottom: `1px solid ${BORDER}` }}>{opp ?? "—"}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: TEXT,
                    borderBottom: `1px solid ${BORDER}` }}>
                    {(vc.venue_volume_shares ?? {})[v] != null ? `${vc.venue_volume_shares[v]}%` : "—"}</td>
                </tr>);
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DexHealth({ dex }: { dex: any }) {
  const profiles = (dex?.profiles ?? []) as any[];
  const warnings = (dex?.warnings ?? []) as string[];
  const bandOf = (p: any, b: number) => p?.depth?.[String(b)] ?? {};
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 12 }}>
        {profiles.map((p) => (
          <div key={`${p.venue}|${p.pair}`} style={card}>
            <div style={lbl}>
              DEX · {String(p.venue ?? "").replaceAll("_", " ")} · {p.pair}
            </div>
            <div style={kpi}>{fmtUsd(p.tvl_usd)}
              <span style={{ fontSize: 13, color: MUTED }}> TVL</span></div>
            <div style={{ fontSize: 12, color: TEXT, marginTop: 6 }}>
              In-range (±{p.concentration_band_bps ?? 200}bps):{" "}
              <b>{p.active_range_concentration_pct ?? "—"}%</b>
              <span style={{ color: MUTED }}>
                {" "}· fee {p.fee_tier_bps != null ? `${p.fee_tier_bps} bps` : "—"} · 7d ADV {fmtUsd(p.adv_usd)}
              </span>
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
              Depth (bid/ask, 24h median):{" "}
              {(p.visible_depth_bands_bps ?? []).map((b: number) => (
                <span key={b} style={{ marginRight: 10 }}>
                  ±{b}bps {fmtUsd(bandOf(p, b).bid_median_usd)}/{fmtUsd(bandOf(p, b).ask_median_usd)}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
              chain {p.chain ?? "—"} · pool state {p.pool_state_ts ? "fresh" : "unavailable"}
            </div>
          </div>
        ))}
      </div>
      {warnings.length > 0 && (
        <div style={{ fontSize: 11, color: MUTED }}>
          Data notes: {warnings.join("; ")}
        </div>
      )}
    </>
  );
}

function RfpConfigForm({ clientId, packId, rfpVenues }: {
  clientId: string; packId: string;
  rfpVenues: { exchange: string; pair: string; status: string; venue_type?: string }[];
}) {
  const [term, setTerm] = useState(180);
  const [models, setModels] = useState<string[]>(["retainer", "call_option", "profit_share"]);
  const [mms, setMms] = useState("");
  // LIQ-120: entity registry for the market-maker picker (operator session).
  // Picked names are the registry's market-maker names, so token binding is exact.
  const [registry, setRegistry] = useState<{ le_id: string; market_maker_name: string; legal_name: string }[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => {
    fetch("/api/v1/mm/registry", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { market_makers: [] }))
      .then((b) => setRegistry(b.market_makers ?? []))
      .catch(() => setRegistry([]));
  }, []);
  const [venues, setVenues] = useState<string[]>(
    rfpVenues.filter((v) => v.status === "active").map((v) => `${v.exchange}|${v.pair}`),
  );
  const [custom, setCustom] = useState<{ exchange: string; pair: string }[]>([]);
  const [cEx, setCEx] = useState("");
  const [cPair, setCPair] = useState("");
  const baseToken =
    rfpVenues.find((v) => v.pair?.includes("/"))?.pair.split("/")[0] ?? "BTC";
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = (arr: string[], set: (v: string[]) => void, val: string) =>
    set(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const generate = () => {
    if (busy) return;
    setBusy(true); setError(null); setResult(null);
    fetch(`/api/v1/governance/clients/${encodeURIComponent(clientId)}/generate-rfp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pack_id: packId,
        venues,
        engagement_term_days: term,
        max_submissions: 5,
        market_makers: [...picked, ...mms.split("\n").map((s) => s.trim()).filter(Boolean)],
        permitted_compensation_models: models,
      }),
    })
      .then(async (r) => {
        const b = await r.json();
        if (!r.ok) throw new Error(b.detail ?? `Failed (${r.status})`);
        setResult(b.rfp_id);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setBusy(false));
  };

  const row = { display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
    marginBottom: 12, fontSize: 13, color: TEXT } as const;

  return (
    <div style={{ marginTop: 14, borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
      <div style={lbl}>RFP configuration — {packId.replaceAll("_", " ")}</div>
      <div style={row}>
        <span style={{ color: MUTED }}>Term:</span>
        {[90, 180, 365].map((t) => (
          <button key={t} onClick={() => setTerm(t)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 5, cursor: "pointer",
              border: `1px solid ${term === t ? ACCENT : "rgba(255,255,255,0.14)"}`,
              background: term === t ? "rgba(102,153,255,0.16)" : "transparent", color: TEXT }}>
            {t}d
          </button>
        ))}
        <span style={{ color: MUTED, marginLeft: 12 }}>Compensation:</span>
        {["retainer", "call_option", "profit_share"].map((m) => (
          <label key={m} style={{ display: "flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={models.includes(m)}
              onChange={() => toggle(models, setModels, m)} />
            {m.replaceAll("_", " ")}
          </label>
        ))}
      </div>
      <div style={{ ...row, alignItems: "flex-start" }}>
        <span style={{ color: MUTED, paddingTop: 6 }}>Venues:</span>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {rfpVenues.filter((v) => v.status === "active").map((v) => {
            const key = `${v.exchange}|${v.pair}`;
            const isDex = v.venue_type === "dex";
            return (
              <label key={key} style={{ display: "flex", gap: 5, alignItems: "center",
                cursor: "pointer", fontSize: 12 }}>
                <input type="checkbox" checked={venues.includes(key)}
                  onChange={() => toggle(venues, setVenues, key)} />
                {v.exchange} · {v.pair}
                {isDex && (
                  <span style={{ fontSize: 10, color: GREEN, border: `1px solid ${GREEN}55`,
                    borderRadius: 4, padding: "0 5px" }}>DEX</span>
                )}
                {v.status === "prospective" && (
                  <span style={{ fontSize: 10, color: AMBER, border: `1px solid ${AMBER}55`,
                    borderRadius: 4, padding: "0 5px" }}>NEW</span>
                )}
              </label>
            );
          })}
          {custom.map((c) => {
            const key = `${c.exchange}|${c.pair}`;
            return (
              <label key={key} style={{ display: "flex", gap: 5, alignItems: "center",
                cursor: "pointer", fontSize: 12 }}>
                <input type="checkbox" checked={venues.includes(key)}
                  onChange={() => toggle(venues, setVenues, key)} />
                {c.exchange} · {c.pair}
                <span style={{ fontSize: 10, color: ACCENT, border: `1px solid ${ACCENT}55`,
                  borderRadius: 4, padding: "0 5px" }}>CUSTOM</span>
              </label>
            );
          })}
        </div>
      </div>
      <div style={row}>
        <span style={{ color: MUTED }}>Add venue:</span>
        <input value={cEx} onChange={(e) => setCEx(e.target.value)} placeholder="venue (e.g. binance)"
          style={{ fontSize: 12, padding: 6, width: 160, color: TEXT, borderRadius: 5,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }} />
        <input value={cPair} onChange={(e) => setCPair(e.target.value)} placeholder={`pair (e.g. ${baseToken}/USDT)`}
          style={{ fontSize: 12, padding: 6, width: 160, color: TEXT, borderRadius: 5,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }} />
        <button
          disabled={!cEx.trim() || !cPair.includes("/")}
          onClick={() => {
            const ex = cEx.trim().toLowerCase(); const pr = cPair.trim().toUpperCase();
            setCustom((cs) => [...cs, { exchange: ex, pair: pr }]);
            setVenues((vs) => [...vs, `${ex}|${pr}`]);
            setCEx(""); setCPair("");
          }}
          style={{ fontSize: 12, padding: "5px 12px", borderRadius: 5, cursor: "pointer",
            border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: TEXT }}>
          + Add
        </button>
      </div>
      <div style={{ ...row, alignItems: "flex-start" }}>
        <span style={{ color: MUTED, paddingTop: 6 }}>MM invites:</span>
        <div style={{ flex: 1, minWidth: "min(280px, 100%)" }}>
          {/* LIQ-120: registry picker — each picked MM receives a unique submission link */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginBottom: 8 }}>
            {registry.length === 0 && (
              <span style={{ fontSize: 12, color: MUTED }}>No market makers in the registry yet.</span>)}
            {registry.map((e) => (
              <label key={e.le_id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={picked.includes(e.market_maker_name)}
                  onChange={() => toggle(picked, setPicked, e.market_maker_name)} />
                <span style={{ color: TEXT }}>{e.market_maker_name}</span>
                {e.legal_name !== e.market_maker_name && (
                  <span style={{ color: MUTED }}>({e.legal_name})</span>)}
              </label>
            ))}
          </div>
          <textarea value={mms} onChange={(e) => setMms(e.target.value)}
            placeholder={"New market makers not in the registry — one per line (a profile is created for each)"}
            rows={2}
            style={{ width: "100%", boxSizing: "border-box", fontSize: 12, color: TEXT, padding: 8,
              background: "rgba(255,255,255,0.05)", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.14)" }} />
        </div>
      </div>
      <button onClick={generate} disabled={busy || venues.length === 0}
        style={{ fontSize: 13, fontWeight: 700, padding: "8px 20px", borderRadius: 6,
          border: "none", cursor: busy ? "default" : "pointer",
          background: busy ? MUTED : ACCENT, color: "#0a0e17" }}>
        {busy ? "Generating…" : "Generate RFP"}
      </button>
      {result && (
        <div style={{ fontSize: 13, color: GREEN, marginTop: 10 }}>
          Generated <b>{result}</b> — submission links are on the engagement detail
          (Engagements tab).
        </div>
      )}
      {error && <div style={{ fontSize: 13, color: RED, marginTop: 10 }}>{error}</div>}
    </div>
  );
}
